import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import PasswordInput from '../../components/PasswordInput';

export default function AdminProfile() {
  const { user, refreshUser, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [savingPassword, setSavingPassword] = useState(false);

  const isSuperAdmin = user?.accountType === 'admin' && (user?.role?.isSystem === true || !user?.role);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profileForm.name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSavingProfile(true);
    try {
      await api.put('/auth/profile', profileForm);
      await refreshUser();
      toast.success('Admin profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setSavingPassword(true);
    try {
      await api.put('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast.success('Password updated successfully! Please keep it secure.');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const allModules = [
    { key: 'dashboard', name: 'Dashboard Overview', desc: 'Real-time sales, order queue & analytics' },
    { key: 'products', name: 'Product Catalog & Variants', desc: 'Idols, malas, agarbatti, inventory stock' },
    { key: 'categories', name: 'Categories & Taxonomy', desc: 'Hierarchy, slugs and display ordering' },
    { key: 'blogs', name: 'Blog & Editorial Content', desc: 'Articles, rich text and sacred stories' },
    { key: 'orders', name: 'Orders & Fulfilment', desc: 'Order tracking, invoice generation & dispatch' },
    { key: 'transactions', name: 'Transactions & Payments', desc: 'Razorpay, Stripe, PayPal & COD ledger' },
    { key: 'promo_codes', name: 'Coupons & Promo Codes', desc: 'Discount rules, usage limits and expiry' },
    { key: 'customers', name: 'Customer Accounts', desc: 'Registered user profiles and order histories' },
    { key: 'leads', name: 'Inquiries & Contact Leads', desc: 'Customer queries, callbacks and notes' },
    { key: 'users_roles', name: 'Roles & Admin Staff', desc: 'Role permissions and admin user management' },
    { key: 'shipping', name: 'Shipping & Delivery Rules', desc: 'Warehouse GPS, rates & zone limits' },
    { key: 'notifications', name: 'Broadcast & Notifications', desc: 'Customer push alerts & email broadcasts' },
    { key: 'settings_general', name: 'Store Branding & Banners', desc: 'Logo, tagline, banners and contact info' },
    { key: 'settings_payment', name: 'Payment Gateways Config', desc: 'Razorpay live keys, Stripe & PayPal setup' },
    { key: 'settings_smtp', name: 'SMTP Email Provider', desc: 'Custom SMTP mail server credentials' },
    { key: 'system', name: 'System Diagnostics & Audit', desc: 'Database health, server metrics & logs' },
  ];

  return (
    <div className="space-y-6 text-[#2B1810] max-w-5xl">
      {/* Top Banner / Hero */}
      <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#C49A45]/10 to-transparent rounded-bl-full pointer-events-none" />
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 relative z-10">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-[#C49A45] to-[#8C6B28] text-white flex items-center justify-center font-heading font-bold text-2xl sm:text-3xl shadow-md border-2 border-white">
                {user?.name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 border-2 border-white rounded-full flex items-center justify-center shadow-sm" title="Active Admin Session">
                <span className="w-2 h-2 bg-white rounded-full" />
              </span>
            </div>

            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#2B1810]">
                  {user?.name || 'Administrator'}
                </h1>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#C49A45]/15 text-[#8C6B28] border border-[#C49A45]/30 flex items-center gap-1.5">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  {isSuperAdmin ? 'Super Admin · Master Access' : user?.role?.name || 'Admin'}
                </span>
              </div>
              <p className="text-xs text-[#8A796C] mt-0.5">{user?.email || 'admin@chandanaaura.com'}</p>
              <div className="flex items-center gap-3 mt-2 text-[11px] text-[#8A796C]">
                {user?.phone && (
                  <span className="flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    {user.phone}
                  </span>
                )}
                <span className="flex items-center gap-1 text-emerald-700 font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Authenticated Session Active
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 self-stretch sm:self-auto">
            <Link
              to="/"
              target="_blank"
              className="flex-1 sm:flex-initial px-3.5 py-2 bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#D9D0C8] text-[#2B1810] text-xs font-bold uppercase rounded-lg transition-colors text-center inline-flex items-center justify-center gap-1.5"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              View Storefront
            </Link>
            <button
              onClick={() => {
                logout();
                toast.success('Logged out successfully');
              }}
              className="flex-1 sm:flex-initial px-3.5 py-2 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-xs font-bold uppercase rounded-lg transition-colors text-center inline-flex items-center justify-center gap-1.5"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Logout
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 border-t border-[#E8DFD8] mt-6 pt-4 overflow-x-auto">
          {[
            { 
              id: 'profile', 
              label: 'Edit Profile Details', 
              icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> 
            },
            { 
              id: 'security', 
              label: 'Password & Security', 
              icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg> 
            },
            { 
              id: 'permissions', 
              label: 'Role & Permissions', 
              icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> 
            },
            { 
              id: 'system', 
              label: 'System Shortcuts', 
              icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg> 
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-[#C49A45] text-white shadow-sm'
                  : 'bg-[#FAF8F5] text-[#2B1810] hover:bg-[#F5EFEB]'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* TAB 1: Profile Details */}
      {activeTab === 'profile' && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-5">
            <div className="border-b border-[#E8DFD8] pb-3">
              <h2 className="font-heading text-lg font-bold text-[#2B1810]">Admin Profile Information</h2>
              <p className="text-xs text-[#8A796C]">Update your administrative display name and mobile contact number.</p>
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  placeholder="e.g. Master Administrator"
                  className="input-field py-2.5"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  disabled
                  value={user?.email || ''}
                  className="input-field py-2.5 bg-gray-100 text-gray-500 cursor-not-allowed"
                />
                <p className="text-[10px] text-[#8A796C] mt-1">Super Admin primary email is locked for security.</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1.5">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={profileForm.phone}
                  onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                  placeholder="e.g. 9876543210"
                  className="input-field py-2.5"
                />
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  type="submit"
                  disabled={savingProfile}
                  className="bg-[#C49A45] hover:bg-[#B08736] disabled:opacity-50 text-white font-bold text-xs uppercase tracking-widest px-8 py-3 rounded-lg shadow transition-all hover:scale-[1.02] inline-flex items-center gap-2"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                  {savingProfile ? 'SAVING CHANGES...' : 'SAVE PROFILE DETAILS'}
                </button>
              </div>
            </form>
          </div>

          <div className="space-y-4">
            <div className="bg-[#FAF8F5] border border-[#E8DFD8] rounded-xl p-5 space-y-3">
              <h3 className="font-heading font-bold text-sm text-[#2B1810]">Account Overview</h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between py-1.5 border-b border-[#E8DFD8]">
                  <span className="text-[#8A796C]">Account Type</span>
                  <span className="font-bold text-[#2B1810] capitalize">{user?.accountType || 'admin'}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[#E8DFD8]">
                  <span className="text-[#8A796C]">Privilege Level</span>
                  <span className="font-bold text-[#C49A45]">{isSuperAdmin ? 'Full Master Access' : 'Custom Role'}</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-[#E8DFD8]">
                  <span className="text-[#8A796C]">Status</span>
                  <span className="font-bold text-emerald-700">✓ Active & Verified</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-[#8A796C]">Two-Factor / Shield</span>
                  <span className="font-bold text-[#2B1810]">JWT Token (7d)</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800 space-y-1">
              <div className="flex items-center gap-1.5 font-bold">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                <span>Security Notice</span>
              </div>
              <p className="text-[11px] leading-relaxed">
                As Super Admin, your account has complete authority to configure payment keys, modify order records, and manage all store databases.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: Password & Security */}
      {activeTab === 'security' && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-5">
            <div className="border-b border-[#E8DFD8] pb-3">
              <h2 className="font-heading text-lg font-bold text-[#2B1810]">Change Admin Password</h2>
              <p className="text-xs text-[#8A796C]">Ensure you use a strong, unique password of at least 8 characters.</p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1.5">
                  Current Password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  required
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  placeholder="Enter your current password"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1.5">
                  New Password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  required
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="At least 8 characters"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1.5">
                  Confirm New Password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  required
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  placeholder="Re-enter new password"
                />
              </div>

              {passwordForm.newPassword && (
                <div className="p-3 bg-[#FAF8F5] border border-[#E8DFD8] rounded-lg text-xs space-y-1">
                  <p className="font-bold text-[#2B1810]">Password Quality Checklist:</p>
                  <ul className="text-[11px] space-y-0.5 text-[#8A796C]">
                    <li className={passwordForm.newPassword.length >= 8 ? 'text-emerald-700 font-semibold' : ''}>
                      {passwordForm.newPassword.length >= 8 ? '✓' : '○'} At least 8 characters long
                    </li>
                    <li className={/[A-Z]/.test(passwordForm.newPassword) ? 'text-emerald-700 font-semibold' : ''}>
                      {/[A-Z]/.test(passwordForm.newPassword) ? '✓' : '○'} Contains uppercase letter (A-Z)
                    </li>
                    <li className={/[0-9]/.test(passwordForm.newPassword) ? 'text-emerald-700 font-semibold' : ''}>
                      {/[0-9]/.test(passwordForm.newPassword) ? '✓' : '○'} Contains number (0-9)
                    </li>
                    <li className={passwordForm.newPassword === passwordForm.confirmPassword && passwordForm.confirmPassword ? 'text-emerald-700 font-semibold' : ''}>
                      {passwordForm.newPassword === passwordForm.confirmPassword && passwordForm.confirmPassword ? '✓ Passwords match' : '○ Passwords match'}
                    </li>
                  </ul>
                </div>
              )}

              <div className="pt-2 flex justify-end">
                <button
                  type="submit"
                  disabled={savingPassword}
                  className="bg-[#C49A45] hover:bg-[#B08736] disabled:opacity-50 text-white font-bold text-xs uppercase tracking-widest px-8 py-3 rounded-lg shadow transition-all hover:scale-[1.02] inline-flex items-center gap-2"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  {savingPassword ? 'UPDATING PASSWORD...' : 'UPDATE PASSWORD'}
                </button>
              </div>
            </form>
          </div>

          <div className="bg-[#FAF8F5] border border-[#E8DFD8] rounded-xl p-5 space-y-3">
            <h3 className="font-heading font-bold text-sm text-[#2B1810]">Security Recommendations</h3>
            <ul className="text-xs text-[#8A796C] space-y-2 list-disc list-inside leading-relaxed">
              <li>Use a password manager to store high-entropy administrative credentials.</li>
              <li>Never share Super Admin login credentials over unencrypted channels.</li>
              <li>Rotate admin passwords quarterly to prevent credential exposure.</li>
              <li>Always logout when accessing from shared or public computers.</li>
            </ul>
          </div>
        </div>
      )}

      {/* TAB 3: Role & Permissions */}
      {activeTab === 'permissions' && (
        <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-[#E8DFD8] pb-4">
            <div>
              <h2 className="font-heading text-lg font-bold text-[#2B1810]">Administrative Permissions Matrix</h2>
              <p className="text-xs text-[#8A796C]">Overview of all authorized store modules and management capabilities.</p>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase bg-emerald-100 text-emerald-800 border border-emerald-300">
              {isSuperAdmin ? 'All 16 Modules Unlocked' : `${user?.role?.permissions?.length || 0} Modules Assigned`}
            </span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {allModules.map((m) => (
              <div key={m.key} className="p-3.5 rounded-xl border border-[#E8DFD8] bg-[#FAF8F5] hover:border-[#C49A45]/50 transition-colors space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs text-[#2B1810]">{m.name}</span>
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 uppercase">
                    Full Access
                  </span>
                </div>
                <p className="text-[10px] text-[#8A796C]">{m.desc}</p>
              </div>
            ))}
          </div>

          <div className="pt-2 flex items-center justify-between flex-wrap gap-3 border-t border-[#E8DFD8]">
            <p className="text-xs text-[#8A796C]">Need to create custom limited admin roles for staff members?</p>
            <Link
              to="/admin/roles"
              className="px-4 py-2 bg-[#2B1810] hover:bg-[#3D251A] text-white text-xs font-bold uppercase rounded-lg shadow transition-colors inline-flex items-center gap-1.5"
            >
              <span>Manage Roles & Permissions</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14m-7-7 7 7-7 7"/></svg>
            </Link>
          </div>
        </div>
      )}

      {/* TAB 4: System Shortcuts */}
      {activeTab === 'system' && (
        <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-5">
          <div className="border-b border-[#E8DFD8] pb-3">
            <h2 className="font-heading text-lg font-bold text-[#2B1810]">Admin Quick Shortcuts & Tools</h2>
            <p className="text-xs text-[#8A796C]">Fast-access navigation to critical management areas.</p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/admin/settings" className="p-4 rounded-xl border border-[#E8DFD8] bg-[#FAF8F5] hover:bg-[#F5EFEB] hover:border-[#C49A45] transition-all space-y-2 group">
              <div className="w-9 h-9 rounded-lg bg-white border border-[#E8DFD8] flex items-center justify-center text-[#8A796C] group-hover:text-[#C49A45] group-hover:border-[#C49A45] transition-colors">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              </div>
              <p className="font-bold text-xs text-[#2B1810] group-hover:text-[#C49A45] transition-colors">Payment Gateways</p>
              <p className="text-[10px] text-[#8A796C]">Razorpay live keys, Stripe & PayPal setup</p>
            </Link>

            <Link to="/admin/notifications" className="p-4 rounded-xl border border-[#E8DFD8] bg-[#FAF8F5] hover:bg-[#F5EFEB] hover:border-[#C49A45] transition-all space-y-2 group">
              <div className="w-9 h-9 rounded-lg bg-white border border-[#E8DFD8] flex items-center justify-center text-[#8A796C] group-hover:text-[#C49A45] group-hover:border-[#C49A45] transition-colors">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 11l18-5v12L3 13v-2z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>
              </div>
              <p className="font-bold text-xs text-[#2B1810] group-hover:text-[#C49A45] transition-colors">Broadcast Alert</p>
              <p className="text-[10px] text-[#8A796C]">Send customer push notifications & emails</p>
            </Link>

            <Link to="/admin/system" className="p-4 rounded-xl border border-[#E8DFD8] bg-[#FAF8F5] hover:bg-[#F5EFEB] hover:border-[#C49A45] transition-all space-y-2 group">
              <div className="w-9 h-9 rounded-lg bg-white border border-[#E8DFD8] flex items-center justify-center text-[#8A796C] group-hover:text-[#C49A45] group-hover:border-[#C49A45] transition-colors">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              </div>
              <p className="font-bold text-xs text-[#2B1810] group-hover:text-[#C49A45] transition-colors">System & Audit Logs</p>
              <p className="text-[10px] text-[#8A796C]">Database health & system diagnostics</p>
            </Link>

            <Link to="/admin/orders" className="p-4 rounded-xl border border-[#E8DFD8] bg-[#FAF8F5] hover:bg-[#F5EFEB] hover:border-[#C49A45] transition-all space-y-2 group">
              <div className="w-9 h-9 rounded-lg bg-white border border-[#E8DFD8] flex items-center justify-center text-[#8A796C] group-hover:text-[#C49A45] group-hover:border-[#C49A45] transition-colors">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
              </div>
              <p className="font-bold text-xs text-[#2B1810] group-hover:text-[#C49A45] transition-colors">Order Fulfilment</p>
              <p className="text-[10px] text-[#8A796C]">Live order queue & dispatch management</p>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
